import { Autocomplete, Box, Button, FormControlLabel, Switch, TextField } from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFieldConfigs, useUpdateFieldConfigs } from '@src/tools/anonymizer/api/field-config-api'
import { CONVERSION_METHODS, type FieldConfigResponse, type FieldConfigUpdate } from '@src/tools/anonymizer/types/Constants'
import { useToast } from '@src/shared/hooks/useToast'

interface FieldConfigGridProps {
  dbName: string
  containerName: string
}

const FieldConfigGrid = ({ dbName, containerName }: FieldConfigGridProps) => {
  const { t } = useTranslation()
  const { showSuccess, showError, ToastComponent } = useToast()
  const [showPendingOnly, setShowPendingOnly] = useState(true)
  const [filterText, setFilterText] = useState('')
  const [editedRows, setEditedRows] = useState<Record<string, string>>({})

  const [configs, isLoading] = useFieldConfigs(dbName, containerName)
  const [updateConfigs, isSaving] = useUpdateFieldConfigs()

  const handleMethodChange = useCallback((id: string, newMethod: string) => {
    setEditedRows((prev) => ({ ...prev, [id]: newMethod }))
  }, [])

  const handleSave = useCallback(async () => {
    const updates: FieldConfigUpdate[] = []
    for (const config of configs) {
      const editedMethod = editedRows[config.id]
      if (editedMethod !== undefined || config.approvedOn === null) {
        updates.push({
          id: config.id,
          conversionMethod: editedMethod ?? config.conversionMethod,
        })
      }
    }

    if (updates.length === 0) return

    try {
      const result = await updateConfigs({ dbName, containerName, updates })
      setEditedRows({})
      const message = result.containerUnblocked
        ? t('anonymizer.saveSuccessUnblocked', { count: result.updated })
        : t('anonymizer.saveSuccess', { count: result.updated })
      showSuccess(message)
    } catch {
      showError(t('anonymizer.saveError'))
    }
  }, [configs, editedRows, updateConfigs, dbName, containerName, t, showSuccess, showError])

  const filteredConfigs = useMemo(() => {
    let result = configs
    if (showPendingOnly) result = result.filter((c) => c.approvedOn === null)
    if (filterText) {
      const lower = filterText.toLowerCase()
      result = result.filter((c) => {
        const fullPath = c.fieldPath ? `${c.fieldPath}.${c.fieldName}` : c.fieldName
        const method = editedRows[c.id] ?? c.conversionMethod
        return fullPath.toLowerCase().includes(lower) || method.toLowerCase().includes(lower)
      })
    }
    return result
  }, [configs, showPendingOnly, filterText, editedRows])

  const hasChanges = useMemo(() => {
    return Object.keys(editedRows).length > 0 || configs.some((c) => c.approvedOn === null)
  }, [configs, editedRows])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'fullPath',
        headerName: t('anonymizer.columns.fieldPath'),
        flex: 1.5,
        valueGetter: (_value: unknown, row: FieldConfigResponse) =>
          row.fieldPath ? `${row.fieldPath}.${row.fieldName}` : row.fieldName,
      },
      {
        field: 'conversionMethod',
        headerName: t('anonymizer.columns.conversionMethod'),
        flex: 1,
        renderCell: (params: GridRenderCellParams<FieldConfigResponse>) => {
          const currentValue = editedRows[params.row.id] ?? params.row.conversionMethod
          return (
            <Autocomplete
              size="small"
              options={[...CONVERSION_METHODS]}
              value={currentValue}
              onChange={(_e, newValue) => {
                if (newValue) handleMethodChange(params.row.id, newValue)
              }}
              disableClearable
              renderInput={(inputParams) => (
                <TextField
                  {...inputParams}
                  variant="standard"
                  slotProps={{ input: { ...inputParams.InputProps, disableUnderline: true } }}
                />
              )}
              sx={{ width: '100%', display: 'flex', alignItems: 'center', height: '100%' }}
            />
          )
        },
      },
      {
        field: 'approvedBy',
        headerName: t('anonymizer.columns.approvedBy'),
        flex: 0.7,
        valueGetter: (_value: unknown, row: FieldConfigResponse) => row.approvedBy ?? '',
      },
      {
        field: 'approvedOn',
        headerName: t('anonymizer.columns.approvedOn'),
        flex: 0.7,
        valueGetter: (_value: unknown, row: FieldConfigResponse) =>
          row.approvedOn ? new Date(row.approvedOn).toLocaleDateString() : '',
      },
      {
        field: 'status',
        headerName: t('anonymizer.columns.status'),
        flex: 0.5,
        renderCell: (params: GridRenderCellParams<FieldConfigResponse>) => {
          const isApproved = params.row.approvedOn !== null
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Box
                sx={{
                  px: 0.75,
                  py: 0,
                  borderRadius: 999,
                  bgcolor: isApproved ? 'success.light' : 'warning.light',
                  color: isApproved ? 'success.dark' : 'warning.dark',
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  lineHeight: 1.6,
                  whiteSpace: 'nowrap',
                }}
              >
                {isApproved ? t('anonymizer.approved') : t('anonymizer.pending')}
              </Box>
            </Box>
          )
        },
      },
    ],
    [t, editedRows, handleMethodChange],
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={<Switch checked={showPendingOnly} onChange={(e) => setShowPendingOnly(e.target.checked)} />}
            label={t('anonymizer.showPendingOnly')}
          />
          <TextField
            size="small"
            placeholder={t('anonymizer.filterPlaceholder')}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{ minWidth: 250 }}
          />
        </Box>
        <Button variant="contained" disabled={!hasChanges || isSaving} onClick={handleSave}>
          {isSaving ? t('anonymizer.saving') : t('anonymizer.saveAndApprove')}
        </Button>
      </Box>
      <DataGrid
        rows={filteredConfigs}
        columns={columns}
        loading={isLoading}
        getRowId={(row: FieldConfigResponse) => row.id}
        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        sx={{ flexGrow: 1 }}
      />
      {ToastComponent()}
    </Box>
  )
}

export default FieldConfigGrid
